import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
	authApiClient,
	PASSWORD_UPDATE_REQUIRED,
	TWO_FACTOR_ENROLLMENT_REQUIRED,
	TWO_FACTOR_REQUIRED,
} from "../../api/authApiClient";
import { handleApiError } from "../../../../shared/utils/handleApiError";
import { loginSchema } from "../../schema";
import { useAuthStore } from "../../store";
import type { LoginCredentials, LoginResponse } from "../../type";
import { TwoFactorChallengeForm } from "./TwoFactorChallengeForm";
import { TwoFactorEnrollmentForm } from "./TwoFactorEnrollmentForm";
import { getDefaultRouteForRole } from "../../permissions";
import { AuthInput } from "../ui/AuthInput";
import { PasswordInput } from "../ui/PasswordInput";
import { AuthCheckbox } from "../ui/AuthCheckbox";
import Button from "../../../../shared/components/Button";

type LoginFormData = LoginCredentials;

export const LoginForm: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { login } = useAuthStore();
	const [formData, setFormData] = useState<LoginFormData>({
		email: "",
		password: "",
		staySignedIn: false,
	});
	const [errors, setErrors] = useState<Partial<LoginFormData>>({});
	const [isLoading, setIsLoading] = useState(false);
	const [serverError, setServerError] = useState<string>("");
	// 2FA challenge (PE-2157). Kept in component state, never persisted: the
	// pendingToken is bearer-equivalent for 5 minutes, so it must not survive a
	// reload or reach the URL. Its presence is also what swaps the rendered step.
	const [pendingToken, setPendingToken] = useState<string | null>(null);
	// Mandated-but-never-enrolled. Since watchman #102 this 401 carries its own
	// pendingToken, which drives the signed-out enrolment screen below.
	const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
	// Fallback for the SAME 401 arriving WITHOUT a token, i.e. an environment
	// still running pre-#102 watchman. There is genuinely no action available
	// there, so it keeps the explanatory dead-end rather than offering a flow
	// whose first call would 401.
	const [enrollmentRequired, setEnrollmentRequired] = useState(false);

	// 2FA status now comes from the server: /auth/me reports twoFactorEnabled and
	// hydrateSession carries it on the response. `passedTwoFactor` survives only as
	// the fallback for a login whose /auth/me enrichment failed — having just cleared
	// a 2FA challenge or enrolment is itself proof it is on, where a plain password
	// login proves nothing either way and stays null (unknown).
	const completeLogin = async (response: LoginResponse, passedTwoFactor = false) => {
		await login(
			response.user,
			response.token,
			formData.staySignedIn,
			response.twoFactorEnabled ?? (passedTwoFactor ? true : null),
		);
		const redirectTo = searchParams.get("redirect_to");
		navigate(redirectTo ?? getDefaultRouteForRole(response.user.roleName));
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev: LoginFormData) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));

		// Clear error messages as soon as user starts typing
		if (errors[name as keyof LoginFormData]) {
			setErrors((prev: Partial<LoginFormData>) => ({
				...prev,
				[name]: undefined,
			}));
		}

		// Also clear any server error when they start typing
		if (serverError) {
			setServerError("");
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setErrors({});
		setServerError("");

		try {
			// Check if the form data is valid before sending to server
			const validatedData = loginSchema.parse(formData);

			// Try to log them in
			const response = await authApiClient.login(validatedData);
			if (response.token && response.user) {
				await completeLogin(response);
			}
		} catch (error: unknown) {
			// First-login forced password update (PE-1667): watchman returns 401 with
			// code PASSWORD_UPDATE_REQUIRED for accounts still on a temporary password.
			// Route to the update screen, carrying the entered credentials so the user
			// doesn't retype the temporary password (never persisted).
			// 2FA challenge (PE-2157): the password was right, but watchman wants a
			// TOTP/backup code before it issues tokens. Swap to the code step in
			// place rather than routing, so the pendingToken never touches the URL
			// or router state (both are readable/persistable).
			const code = (error as { code?: string })?.code;
			if (code === TWO_FACTOR_REQUIRED) {
				const token = (error as { pendingToken?: string }).pendingToken;
				if (token) {
					setPendingToken(token);
					return;
				}
				// A TWO_FACTOR_REQUIRED with no pendingToken is unusable — fall
				// through to the generic error rather than showing a code box that
				// can never succeed.
			}
			// Mandated-but-unenrolled. watchman #102 made this recoverable: the 401
			// carries a pendingToken that the @Public /auth/2fa/enroll pair accepts,
			// so enrolment can run from a signed-out state. Without a token there is
			// still nothing to do, so that case keeps the old explanation.
			if (code === TWO_FACTOR_ENROLLMENT_REQUIRED) {
				const token = (error as { pendingToken?: string }).pendingToken;
				if (token) {
					setEnrollmentToken(token);
				} else {
					setEnrollmentRequired(true);
				}
				return;
			}
			if (
				error &&
				typeof error === "object" &&
				(error as { code?: string }).code === PASSWORD_UPDATE_REQUIRED
			) {
				navigate("/force-password-update", {
					state: { email: formData.email, oldPassword: formData.password },
				});
				return;
			}
			if (
				error &&
				typeof error === "object" &&
				"errors" in error &&
				Array.isArray((error as { errors: unknown }).errors)
			) {
				// Zod validation errors - safe type casting
				const fieldErrors: Partial<LoginFormData> = {};
				const zodError = error as {
					errors: Array<{ path?: string[]; message: string }>;
				};
				zodError.errors.forEach((err) => {
					if (err.path && err.path[0] && typeof err.message === "string") {
						const field = err.path[0];
						// Only assign to valid form fields
						if (
							field === "email" ||
							field === "password" ||
							field === "staySignedIn"
						) {
							const typedFieldErrors = fieldErrors as Record<string, string>;
							typedFieldErrors[field] = err.message;
						}
					}
				});
				setErrors(fieldErrors);
			} else {
				console.error("Login error:", error);
				// Server error - show user-friendly message
				setServerError(handleApiError(error));
			}
		} finally {
			// Always stop the loading spinner, whether success or failure
			setIsLoading(false);
		}
	};

	if (pendingToken) {
		return (
			<TwoFactorChallengeForm
				pendingToken={pendingToken}
				onVerified={(response) => completeLogin(response, true)}
				onExpired={() => {
					setPendingToken(null);
					setServerError("That verification window expired. Please sign in again.");
				}}
				onCancel={() => {
					setPendingToken(null);
					setFormData({ email: "", password: "", staySignedIn: false });
				}}
			/>
		);
	}

	if (enrollmentToken) {
		return (
			<TwoFactorEnrollmentForm
				pendingToken={enrollmentToken}
				// Enrolling proves possession of the new secret, so this session has
				// demonstrably passed 2FA — same as the challenge path.
				onEnrolled={(response) => completeLogin(response, true)}
				onExpired={() => {
					setEnrollmentToken(null);
					setServerError("That setup window expired. Please sign in again.");
				}}
				onCancel={() => {
					setEnrollmentToken(null);
					setFormData({ email: "", password: "", staySignedIn: false });
				}}
			/>
		);
	}

	if (enrollmentRequired) {
		return (
			<div className="space-y-6 text-center">
				<h1 className="text-[28px] font-bold text-foreground">
					Two-step verification required
				</h1>
				<p className="text-sm text-muted-foreground">
					Your password was accepted, but this account must have two-step verification
					set up before it can sign in — and it has not been set up yet.
				</p>
				<p className="text-sm text-muted-foreground">
					Setting it up needs a signed-in session, which this account cannot get until
					it is set up. Ask a TCS administrator to reset two-step verification on your
					account, then sign in again.
				</p>
				<button
					type="button"
					onClick={() => {
						setEnrollmentRequired(false);
						setFormData({ email: "", password: "", staySignedIn: false });
					}}
					className="text-sm text-foreground font-medium hover:underline transition-colors">
					Back to sign in
				</button>
			</div>
		);
	}

	return (
		<div className="space-y-8">
			<div className="text-center">
				<h1 className="text-[28px] font-bold text-foreground mb-2">Sign In</h1>
				<p className="text-sm text-muted-foreground">
					Sign in to your admin account for The Collective Synergy.
				</p>
			</div>

			<form
				onSubmit={handleSubmit}
				className="space-y-6">
				<AuthInput
					name="email"
					type="email"
					placeholder="Email"
					value={formData.email}
					onChange={handleChange}
					error={errors.email}
					required
				/>
				<div className="space-y-1">
					<PasswordInput
						name="password"
						placeholder="Password"
						value={formData.password}
						onChange={handleChange}
						error={errors.password}
						required
					/>
					<div className="items-start flex px-1">
						{serverError && (
							<div className="text-destructive text-sm font-normal text-center">
								{serverError}
							</div>
						)}
					</div>
				</div>

				<div className="flex items-center justify-between">
					<AuthCheckbox
						name="staySignedIn"
						label="Stay signed in"
						checked={formData.staySignedIn}
						onChange={handleChange}
					/>

					<Link
						to="/forgot-password"
						className="text-sm text-foreground font-medium hover:underline transition-colors">
						Forgot your password?
					</Link>
				</div>

				<Button
					type="submit"
					loading={isLoading}
					className="w-full">
					Log In
				</Button>

				{/* Merchant self-onboarding entry point (PE-2097). Public route —
				    applying creates no session, so it sits outside the form flow. */}
				<p className="text-sm text-muted-foreground text-center">
					Want to sell with us?{" "}
					<Link
						to="/onboard/signup"
						className="text-foreground font-medium hover:underline transition-colors">
						Apply here
					</Link>
				</p>
			</form>
		</div>
	);
};
